import { ChevronUpCircle, Settings, Upload, Users } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import useBreakpoint from "@/app/utils/use-breakpoint";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useIsModerator } from "@/features/participants/api/is-moderator";
import { useVidyoStore } from "@/features/vidyo-call/utils/vidyo-store";
import {
  shareScreen,
  stopShareScreen,
} from "@/features/vidyo-screen-share/api/share-screen";
import { cn } from "@/lib/utils";

import { ActionButton } from "./action-button";
import { LeaveCall } from "./action-buttons/leave-call";
import { RaiseHand } from "./action-buttons/raise-hand";
import { RecordCall } from "./action-buttons/record-call";
import { ToggleCamera } from "./action-buttons/toggle-camera";
import { ToggleCaption } from "./action-buttons/toggle-caption";
import { ToggleChat } from "./action-buttons/toggle-chat";
import { ToggleMicrophone } from "./action-buttons/toggle-microphone";
import { ToggleModeration } from "./action-buttons/toggle-moderation";

type Option = {
  element: React.ReactNode;
  /**
   * Some buttons have a different width, eg.:
   * - Leave Session
   */
  width?: number;
};

export function ActionBar() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [visibleOptions, setVisibleOptions] = useState<Option[]>([]);
  const [dropdownOptions, setDropdownOptions] = useState<Option[]>([]);
  const breakpoint = useBreakpoint();

  const openSettingsDrawer = useVidyoStore((s) => s.settingsDrawer.open);
  const closeSettingsDrawer = useVidyoStore((s) => s.settingsDrawer.close);
  const isSettingsDrawerOpen = useVidyoStore((s) => s.settingsDrawer.isOpen);
  const openParticipantsDrawer = useVidyoStore(
    (s) => s.participantsDrawer.open,
  );
  const closeParticipantsDrawer = useVidyoStore(
    (s) => s.participantsDrawer.close,
  );
  const isParticipantsDrawerOpen = useVidyoStore(
    (s) => s.participantsDrawer.isOpen,
  );
  const isSharing = useVidyoStore((state) => state.screenShare.isSharing);
  const isParticipantScreenSharing = useVidyoStore(
    (state) => state.screenShare.isParticipantScreenSharing,
  );
  const isModerator = useIsModerator();

  const handleShare = async () => {
    await shareScreen();
  };

  const handleStopShare = async () => {
    await stopShareScreen();
  };

  const canIShareMyScreen = useMemo(() => {
    return isModerator || isParticipantScreenSharing;
  }, [isModerator, isParticipantScreenSharing]);

  const desktopButtons: Option[] = [
    {
      element: <ToggleMicrophone key="microphone" />,
    },
    {
      element: <ToggleCamera key="camera" />,
    },
    {
      element: <ToggleChat key="chat" />,
    },
    {
      element: <RaiseHand key="raise-hand" />,
    },
    {
      element: (
        <ActionButton
          onClick={isSharing ? handleStopShare : handleShare}
          Icon={Upload}
          text={isSharing ? "Stop Sharing" : "Share"}
          isActive={isSharing}
          disabled={!canIShareMyScreen}
          key="share"
        />
      ),
    },

    {
      element: <LeaveCall key="leave-session" />,
    },
  ];

  const mobileButtons: Option[] = useMemo(
    () => [
      {
        element: <ToggleMicrophone key="microphone" />,
      },
      {
        element: <ToggleCamera key="camera" />,
      },
      {
        element: <LeaveCall key="leave-session" />,
        width: 114,
      },
      {
        element: <RaiseHand key="raise-hand" />,
      },
      {
        element: (
          <ActionButton
            onClick={isSharing ? handleStopShare : handleShare}
            Icon={Upload}
            text={isSharing ? "Stop Sharing" : "Share"}
            isActive={isSharing}
            disabled={!canIShareMyScreen}
            key="share"
          />
        ),
      },
      {
        element: <RecordCall key="record" />,
      },
      {
        element: <ToggleCaption key="caption" />,
      },
      {
        element: <ToggleChat key="chat" />,
      },
      {
        element: (
          <ActionButton
            onClick={
              isParticipantsDrawerOpen
                ? closeParticipantsDrawer
                : openParticipantsDrawer
            }
            Icon={Users}
            text="Participants"
            key="participants"
            isActive={isParticipantsDrawerOpen}
          />
        ),
      },
      {
        element: (
          <ActionButton
            onClick={
              isSettingsDrawerOpen ? closeSettingsDrawer : openSettingsDrawer
            }
            Icon={Settings}
            text="Settings"
            key="settings"
            isActive={isSettingsDrawerOpen}
          />
        ),
      },
      {
        element: <ToggleModeration key="moderation" />,
      },
    ],
    [
      canIShareMyScreen,
      isParticipantsDrawerOpen,
      isSettingsDrawerOpen,
      closeParticipantsDrawer,
      openParticipantsDrawer,
      closeSettingsDrawer,
      isSharing,
      openSettingsDrawer,
    ],
  );

  const isDesktop =
    breakpoint === "lg" || breakpoint === "xl" || breakpoint === "2xl";

  useEffect(() => {
    if (isDesktop) return;
    const updateLayout = () => {
      const container = containerRef.current;
      if (!container) return;
      const buttonWidth = 100; // Replace with actual button width logic if needed

      const availableWidth = container.offsetWidth;
      let usedWidth = buttonWidth;
      const tempVisibleOptions: Option[] = [];
      const tempDropdownOptions: Option[] = [];

      mobileButtons.forEach((option) => {
        if (usedWidth + (option.width ?? buttonWidth) <= availableWidth) {
          tempVisibleOptions.push(option);
          usedWidth += option.width ?? buttonWidth;
        } else {
          tempDropdownOptions.push(option);
        }
      });

      setVisibleOptions(tempVisibleOptions);
      setDropdownOptions(tempDropdownOptions);
    };

    updateLayout();

    window.addEventListener("resize", updateLayout);
    return () => window.removeEventListener("resize", updateLayout);
  }, [mobileButtons, breakpoint, isDesktop]);

  if (isDesktop) {
    return (
      <TooltipProvider>
        <div
          className={cn(
            "[&>.action-button-container:last-of-type>.action-button]:rounded-r-[calc(var(--radius)-3px)]",
            "[&>.action-button-container:first-of-type>.action-button]:rounded-l-[calc(var(--radius)-3px)]",
            "inline-flex w-auto items-center rounded-md border border-input ring-4 ring-card [&>.action-button-container:last-of-type]:border-r-0 [&>.action-button-container]:border-r [&>.action-button-container]:border-input",
          )}
        >
          {desktopButtons.map((button) => button.element)}
        </div>
      </TooltipProvider>
    );
  }

  return (
    <div ref={containerRef} className="flex w-full justify-center">
      <TooltipProvider>
        <div
          className={cn(
            "[&>.action-button-container:last-of-type>.action-button]:rounded-r-[calc(var(--radius)-3px)]",
            "[&>.action-button-container:first-of-type>.action-button]:rounded-l-[calc(var(--radius)-3px)]",
            "inline-flex w-auto items-center rounded-md border border-input ring-4 ring-card [&>.action-button-container:last-of-type]:border-r-0 [&>.action-button-container]:border-r [&>.action-button-container]:border-input",
          )}
        >
          {visibleOptions.map((button) => button.element)}
          {dropdownOptions.length > 0 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <ActionButton
                  onClick={() => {}}
                  Icon={ChevronUpCircle}
                  text="More"
                />
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="grid w-56 grid-cols-2 bg-background"
                align="end"
                side="bottom"
                sideOffset={8}
              >
                {dropdownOptions.map((option, index) => (
                  <DropdownMenuItem key={`dropdown-item-${index}`} asChild>
                    {option.element}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </TooltipProvider>
    </div>
  );
}
